import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

// Detecta un nuevo despliegue comparando el hash del build (asset-manifest.json de CRA)
// y muestra una barra "Nueva actualización · Actualizar ahora" en TODOS los portales.
async function fetchBuildId() {
  try {
    const r = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    const files = j.files || {};
    const id = `${files["main.js"] || ""}|${files["main.css"] || ""}`;
    return id.length > 1 ? id : null;
  } catch {
    return null; // en desarrollo no existe el manifest -> no pasa nada
  }
}

async function applyUpdate() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        try { reg.waiting && reg.waiting.postMessage({ type: "SKIP_WAITING" }); } catch { /* noop */ }
        try { await reg.update(); } catch { /* noop */ }
      }
    }
    if (typeof caches !== "undefined" && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* noop */ }
  // Recarga forzada para traer el código nuevo
  window.location.reload();
}

// Versión que REALMENTE está corriendo en esta pestaña (aunque venga de caché vieja).
function runningMainJs() {
  const s = Array.from(document.scripts).find((x) => /\/static\/js\/main\.[^/]+\.js/.test(x.src || ""));
  return s ? new URL(s.src).pathname : null;
}

export default function UpdateBanner() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const initial = useRef(null);
  const stopped = useRef(false);

  // En el KIOSKO (pantalla desatendida que queda abierta todo el día) la actualización
  // se aplica SOLA al detectar un despliegue nuevo, sin que nadie toque un botón. En los
  // demás portales mostramos la barra para no interrumpir a alguien a media acción.
  const isKiosk = () => typeof window !== "undefined" && window.location.pathname.startsWith("/kiosk");
  const triggerUpdate = () => {
    stopped.current = true;
    if (isKiosk()) {
      setShow(true);
      setTimeout(() => { applyUpdate(); }, 6000); // breve margen para no cortar un escaneo
    } else {
      setShow(true);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => { initial.current = await fetchBuildId(); check(); })();
    const check = async () => {
      if (stopped.current) return;
      const now = await fetchBuildId();
      if (!alive || !now) return;
      if (!initial.current) { initial.current = now; return; } // adopta línea base si faltó
      const running = runningMainJs();
      const latestMain = now.split("|")[0];
      if (now !== initial.current || (running && latestMain && running !== latestMain)) {
        triggerUpdate();
      }
    };
    const iv = setInterval(check, 30000);
    const onFocus = () => check();
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onFocus);
    // Respaldo: si el Service Worker ya tiene una versión nueva instalada/en espera, avisar.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg || !alive) return;
        if (reg.waiting) { triggerUpdate(); }
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) { triggerUpdate(); }
          });
        });
      }).catch(() => {});
    }
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pageshow", onFocus); };
  }, []);

  if (!show) return null;

  const kiosk = isKiosk();

  return (
    <div
      data-testid="update-banner"
      className="fixed top-0 inset-x-0 z-[1000] bg-navy text-white shadow-lg"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", animation: "uaicardSlideDown .35s ease-out" }}
    >
      <style>{`@keyframes uaicardSlideDown{from{transform:translateY(-100%)}to{transform:translateY(0)}}`}</style>
      <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 py-2.5">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
        </span>
        <p className="flex-1 min-w-0 text-sm font-medium">
          {kiosk
            ? <>Nueva actualización detectada. <span className="hidden sm:inline text-white/80">Aplicando automáticamente…</span></>
            : <>Nueva actualización disponible. <span className="hidden sm:inline text-white/80">Actualiza para ver los últimos cambios; no perderás tu sesión ni tus datos.</span></>}
        </p>
        <button
          onClick={async () => { setBusy(true); await applyUpdate(); }}
          disabled={busy}
          data-testid="update-now-btn"
          className="shrink-0 inline-flex items-center gap-1.5 bg-white text-navy text-sm font-bold rounded-full px-4 py-1.5 hover:bg-nardo-100 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {kiosk ? "Actualizar ya" : "Actualizar ahora"}
        </button>
        {!kiosk && (
          <button
            onClick={() => setShow(false)}
            data-testid="update-dismiss-btn"
            className="shrink-0 text-white/70 hover:text-white p-1"
            title="Ahora no"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
